'use client'

import * as UI from '../footer.styled'
import {FeedbackData, FeedbackSchema} from '../footer.types'
import React, {useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {useMutation} from '@tanstack/react-query'

import {Routes} from '@/src/config/routes'

import {FormProvider} from '@/src/shared/modules/form'

import {InvisibleCaptcha, useCaptcha} from '@/src/shared/modules/captcha'
import Button from '@/src/shared/ui/button'
import {LicenseAgreement} from '@/src/shared/ui/form/agreement'
import FormPhoneInput from '@/src/shared/ui/inputs/form-phone-input'
import {FormInput} from '@/src/shared/ui/inputs/input'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import {getClient} from '@/src/shared/utils/api-factory'
import {useLayoutStore} from '@/src/shared/ui/layout'
import {observer} from 'mobx-react'
import {handleMutationException} from '../../../../../../../../web/src/shared/utils/error'

interface FeedbackModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

export const FeedbackModal: React.FC<FeedbackModalProps> = observer(({open, onClose}) => {
  const layoutStore = useLayoutStore()

  return (
    <UI.FeedbackModal
      key={`feedback-${open ? '+' : '-'}`}
      open={open}
      onCancel={onClose}
      title="Обратная связь">
      <FormProvider
        schema={FeedbackSchema}
        data={{
          name: layoutStore.data.session?.user?.name,
          phone: layoutStore.data.session?.user?.phone,
          email: layoutStore.data.session?.user?.email,
        }}>
        <FeedbackForm />
      </FormProvider>
    </UI.FeedbackModal>
  )
})

const FeedbackForm: React.FC = () => {
  const form = useFormContext<FeedbackData>()
  const [formSubmitted, setSubmitted] = useState(false)

  const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} = useCaptcha()

  const submitMutation = useMutation({
    mutationFn: async (data: FeedbackData) => {
      const client = getClient()
      await client.post(Routes.webApi.form.question.save, {data})
    },
    async onSuccess() {
      setSubmitted(true)
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
  })

  const onSubmit = form.handleSubmit(data => {
    submitMutation.mutate(data)
  })

  if (formSubmitted) {
    return (
      <UI.FeedbackForm $submitted>
        <p>Ваша заявка принята</p>
        <p>Наш менеджер свяжется с вами в ближайшее время.</p>
      </UI.FeedbackForm>
    )
  }

  return (
    <UI.FeedbackForm $submitted={false}>
      <InvisibleCaptcha
        key={renderKey}
        renderKey={renderKey}
        onSuccess={onSuccessToken}
        visible={visibleCaptcha}
        setVisible={setVisibleCaptcha}
      />
      <FormInput name="name" required>
        Имя
      </FormInput>
      <FormPhoneInput name="phone" required>
        Телефон
      </FormPhoneInput>
      <FormInput name="email" required>
        Почта
      </FormInput>
      <FormInput name="question" type="textarea">
        Сообщение
      </FormInput>
      <Button onClick={onSubmit} disabled={!token} loading={!token}>
        Отправить
      </Button>
      <LicenseAgreement btnName="Отправить" />
      {submitMutation.isPending && <AsyncProcessLoading />}
    </UI.FeedbackForm>
  )
}
